import React, { useEffect, useMemo, useState } from 'react';
import {
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  FormControl,
  Grid,
  InputLabel,
  MenuItem,
  Select,
  Tab,
  Tabs,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
  TextField,
  Typography,
  Paper
} from '@mui/material';
import axios from 'axios';
import {
  formatRoute,
  getStatusColor,
  getAircraftName,
  getPilotNames
} from './OperationsBoard-part2';

const formatDateTime = (date) => {
  if (!date) return 'N/A';
  const d = new Date(date);
  if (Number.isNaN(d.getTime())) return 'N/A';
  return d.toLocaleString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit'
  });
};

const getMediaFileName = (media, fallbackLabel) => {
  const fileUrl = media?.fileUrl || '';
  const parts = fileUrl.split('/');
  const fromUrl = parts[parts.length - 1];
  return fromUrl || fallbackLabel;
};

const extractTripKitList = (sector) => {
  const tripKitMedia = (sector?.tripKitMediaIds && sector.tripKitMediaIds.length > 0)
    ? sector.tripKitMediaIds
    : (sector?.tripKitMediaId ? [sector.tripKitMediaId] : []);
  return tripKitMedia.filter(Boolean);
};

const extractWeatherMediaList = (sector) => (
  sector?.weatherMediaId ? [sector.weatherMediaId] : []
);

const sortRows = (rows, sortConfig, accessors) => {
  if (!sortConfig?.field || !accessors?.[sortConfig.field]) return rows;
  const dir = sortConfig.direction === 'desc' ? -1 : 1;
  const accessor = accessors[sortConfig.field];
  return [...rows].sort((a, b) => {
    const av = accessor(a);
    const bv = accessor(b);
    if (av == null && bv == null) return 0;
    if (av == null) return -1 * dir;
    if (bv == null) return 1 * dir;
    if (typeof av === 'number' && typeof bv === 'number') return (av - bv) * dir;
    return String(av).localeCompare(String(bv), undefined, { sensitivity: 'base', numeric: true }) * dir;
  });
};

const toCsvValue = (value) => {
  if (value == null) return '';
  const normalized = String(value).replace(/"/g, '""');
  return `"${normalized}"`;
};

const downloadCsv = (fileName, headers, rows) => {
  const csvContent = [
    headers.map((header) => toCsvValue(header)).join(','),
    ...rows.map((row) => row.map((cell) => toCsvValue(cell)).join(','))
  ].join('\n');

  const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
  const url = window.URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.setAttribute('download', fileName);
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  window.URL.revokeObjectURL(url);
};

const CharterHistoryByCharter = ({ pilots, aircraft, showToast, buildErrorMessage }) => {
  const [loadingTrips, setLoadingTrips] = useState(false);
  const [loadingTripDetail, setLoadingTripDetail] = useState(false);
  const [trips, setTrips] = useState([]);
  const [selectedTrip, setSelectedTrip] = useState(null);
  const [tripSectors, setTripSectors] = useState([]);
  const [tripSort, setTripSort] = useState({ field: 'createdAt', direction: 'desc' });
  const [sectorSort, setSectorSort] = useState({ field: 'seqNo', direction: 'asc' });
  const [filters, setFilters] = useState({
    search: '',
    status: '',
    from: '',
    to: ''
  });

  useEffect(() => {
    const fetchTrips = async () => {
      try {
        setLoadingTrips(true);
        const token = localStorage.getItem('token');
        const response = await axios.get('/api/sales/trips', {
          headers: { Authorization: `Bearer ${token}` }
        });
        setTrips(response.data || []);
      } catch (error) {
        showToast(buildErrorMessage('Load charter history', error), 'error');
      } finally {
        setLoadingTrips(false);
      }
    };
    fetchTrips();
  }, [buildErrorMessage, showToast]);

  const filteredTrips = useMemo(() => {
    const search = filters.search.trim().toLowerCase();
    const fromDate = filters.from ? new Date(`${filters.from}T00:00:00`) : null;
    const toDate = filters.to ? new Date(`${filters.to}T23:59:59`) : null;
    return (trips || []).filter((trip) => {
      const searchable = [
        trip.charterNumber,
        trip.title,
        trip.clientName,
        trip.createdBy?.firstName,
        trip.createdBy?.lastName,
        trip.createdBy?.email,
        getAircraftName(trip.listAircraftId, aircraft)
      ].filter(Boolean).join(' ').toLowerCase();
      if (search && !searchable.includes(search)) return false;
      if (filters.status && trip.status !== filters.status) return false;
      if (fromDate || toDate) {
        const tripDate = trip.createdAt ? new Date(trip.createdAt) : null;
        if (!tripDate || Number.isNaN(tripDate.getTime())) return false;
        if (fromDate && tripDate < fromDate) return false;
        if (toDate && tripDate > toDate) return false;
      }
      return true;
    });
  }, [aircraft, filters.from, filters.search, filters.status, filters.to, trips]);

  const fetchTripDetail = async (tripId) => {
    try {
      setLoadingTripDetail(true);
      const token = localStorage.getItem('token');
      const response = await axios.get(`/api/sales/trips/${tripId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setSelectedTrip(response.data?.trip || null);
      setTripSectors(response.data?.sectors || []);
    } catch (error) {
      showToast(buildErrorMessage('Load charter details', error), 'error');
      setSelectedTrip(null);
      setTripSectors([]);
    } finally {
      setLoadingTripDetail(false);
    }
  };

  const statuses = useMemo(
    () => Array.from(new Set((trips || []).map((trip) => trip.status).filter(Boolean))),
    [trips]
  );

  const handleTripSort = (field) => {
    setTripSort((prev) => ({
      field,
      direction: prev.field === field && prev.direction === 'asc' ? 'desc' : 'asc'
    }));
  };

  const handleSectorSort = (field) => {
    setSectorSort((prev) => ({
      field,
      direction: prev.field === field && prev.direction === 'asc' ? 'desc' : 'asc'
    }));
  };

  const sortedTrips = useMemo(() => (
    sortRows(filteredTrips, tripSort, {
      charterNumber: (t) => t.charterNumber || '',
      status: (t) => t.status || '',
      createdAt: (t) => t.createdAt ? new Date(t.createdAt).getTime() : 0,
      aircraft: (t) => getAircraftName(t.listAircraftId, aircraft) || ''
    })
  ), [aircraft, filteredTrips, tripSort]);

  const sortedTripSectors = useMemo(() => (
    sortRows(tripSectors || [], sectorSort, {
      seqNo: (s) => Number(s.seqNo) || 0,
      route: (s) => formatRoute(s),
      aircraft: (s) => getAircraftName(s.aircraftId, aircraft),
      pilots: (s) => getPilotNames(s.assignedPilots || [], pilots || []),
      status: (s) => s.status || '',
      tripKit: (s) => extractTripKitList(s).length,
      weather: (s) => extractWeatherMediaList(s).length
    })
  ), [tripSectors, sectorSort, aircraft, pilots]);

  const handleDownloadBaVideo = async (sector) => {
    const directUrl = sector?.baMediaId?.fileUrl;
    if (directUrl) {
      window.open(directUrl, '_blank', 'noopener,noreferrer');
      return;
    }
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`/api/media/entity/Sector/${sector._id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const baMedia = (response.data || []).find((item) => item.kind === 'BA_VIDEO' && item.fileUrl);
      if (!baMedia) {
        showToast(`No BA video found for Sector ${sector.seqNo}.`, 'info');
        return;
      }
      window.open(baMedia.fileUrl, '_blank', 'noopener,noreferrer');
    } catch (error) {
      showToast(buildErrorMessage('Download BA video', error), 'error');
    }
  };

  const handleExportTripsCsv = () => {
    if (sortedTrips.length === 0) {
      showToast('No charter history data available to export.', 'info');
      return;
    }

    const rows = sortedTrips.map((trip) => ([
      trip.charterNumber || 'N/A',
      trip.title || 'Untitled Charter',
      trip.clientName || 'N/A',
      getAircraftName(trip.listAircraftId, aircraft) || 'N/A',
      trip.status || 'N/A',
      formatDateTime(trip.createdAt),
      `${trip.createdBy?.firstName || ''} ${trip.createdBy?.lastName || ''}`.trim() || trip.createdBy?.email || 'N/A'
    ]));

    downloadCsv(
      `charter-history-${new Date().toISOString().slice(0, 10)}.csv`,
      ['Charter', 'Title', 'Client', 'Aircraft', 'Status', 'Created', 'Created By'],
      rows
    );
  };

  return (
    <Grid container spacing={3}>
      <Grid item xs={12} md={5}>
        <Card>
          <CardContent>
            <Typography variant="h6" gutterBottom>Filter by Charter</Typography>
            <Grid container spacing={1.5} sx={{ mb: 2 }}>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  size="small"
                  label="Search charter/client"
                  value={filters.search}
                  onChange={(e) => setFilters((prev) => ({ ...prev, search: e.target.value }))}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <FormControl fullWidth size="small">
                  <InputLabel>Status</InputLabel>
                  <Select
                    label="Status"
                    value={filters.status}
                    onChange={(e) => setFilters((prev) => ({ ...prev, status: e.target.value }))}
                  >
                    <MenuItem value="">All</MenuItem>
                    {statuses.map((status) => (
                      <MenuItem key={status} value={status}>{status}</MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  size="small"
                  label="From"
                  type="date"
                  InputLabelProps={{ shrink: true }}
                  value={filters.from}
                  onChange={(e) => setFilters((prev) => ({ ...prev, from: e.target.value }))}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  size="small"
                  label="To"
                  type="date"
                  InputLabelProps={{ shrink: true }}
                  value={filters.to}
                  onChange={(e) => setFilters((prev) => ({ ...prev, to: e.target.value }))}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <Button
                  fullWidth
                  size="small"
                  variant="outlined"
                  onClick={() => setFilters({ search: '', status: '', from: '', to: '' })}
                >
                  Reset Filters
                </Button>
              </Grid>
              <Grid item xs={12} sm={6}>
                <Button
                  fullWidth
                  size="small"
                  variant="contained"
                  onClick={handleExportTripsCsv}
                >
                  Download CSV
                </Button>
              </Grid>
            </Grid>

            <TableContainer component={Paper} sx={{ maxHeight: 420 }}>
              <Table stickyHeader size="small">
                <TableHead>
                  <TableRow>
                    <TableCell sortDirection={tripSort.field === 'charterNumber' ? tripSort.direction : false}>
                      <TableSortLabel
                        active={tripSort.field === 'charterNumber'}
                        direction={tripSort.field === 'charterNumber' ? tripSort.direction : 'asc'}
                        onClick={() => handleTripSort('charterNumber')}
                      >
                        Charter
                      </TableSortLabel>
                    </TableCell>
                    <TableCell sortDirection={tripSort.field === 'aircraft' ? tripSort.direction : false}>
                      <TableSortLabel
                        active={tripSort.field === 'aircraft'}
                        direction={tripSort.field === 'aircraft' ? tripSort.direction : 'asc'}
                        onClick={() => handleTripSort('aircraft')}
                      >
                        Aircraft
                      </TableSortLabel>
                    </TableCell>
                    <TableCell sortDirection={tripSort.field === 'status' ? tripSort.direction : false}>
                      <TableSortLabel
                        active={tripSort.field === 'status'}
                        direction={tripSort.field === 'status' ? tripSort.direction : 'asc'}
                        onClick={() => handleTripSort('status')}
                      >
                        Status
                      </TableSortLabel>
                    </TableCell>
                    <TableCell sortDirection={tripSort.field === 'createdAt' ? tripSort.direction : false}>
                      <TableSortLabel
                        active={tripSort.field === 'createdAt'}
                        direction={tripSort.field === 'createdAt' ? tripSort.direction : 'asc'}
                        onClick={() => handleTripSort('createdAt')}
                      >
                        Created
                      </TableSortLabel>
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {sortedTrips.map((trip) => (
                    <TableRow
                      key={trip._id}
                      hover
                      onClick={() => fetchTripDetail(trip._id)}
                      sx={{
                        cursor: 'pointer',
                        backgroundColor: selectedTrip?._id === trip._id ? 'rgba(25, 118, 210, 0.12)' : 'inherit'
                      }}
                    >
                      <TableCell>{trip.charterNumber || 'N/A'}</TableCell>
                      <TableCell>{getAircraftName(trip.listAircraftId, aircraft) || '—'}</TableCell>
                      <TableCell>
                        <Chip size="small" label={trip.status || 'N/A'} color={getStatusColor(trip.status)} />
                      </TableCell>
                      <TableCell>{formatDateTime(trip.createdAt)}</TableCell>
                    </TableRow>
                  ))}
                  {!loadingTrips && filteredTrips.length === 0 && (
                    <TableRow>
                      <TableCell colSpan={4} align="center">No charters found</TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          </CardContent>
        </Card>
      </Grid>

      <Grid item xs={12} md={7}>
        <Card>
          <CardContent>
            <Typography variant="h6" gutterBottom>
              Charter Details {selectedTrip ? `- ${selectedTrip.charterNumber}` : ''}
            </Typography>
            {!selectedTrip && (
              <Typography variant="body2" color="text.secondary">
                Select a charter to view full history and uploads.
              </Typography>
            )}
            {selectedTrip && (
              <>
                <Box sx={{ mb: 2 }}>
                  <Typography variant="subtitle2">{selectedTrip.title || 'Untitled Charter'}</Typography>
                  <Typography variant="body2" color="text.secondary">
                    Client: {selectedTrip.clientName || 'N/A'} | Created: {formatDateTime(selectedTrip.createdAt)}
                  </Typography>
                </Box>
                <TableContainer component={Paper}>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell sortDirection={sectorSort.field === 'seqNo' ? sectorSort.direction : false}>
                          <TableSortLabel
                            active={sectorSort.field === 'seqNo'}
                            direction={sectorSort.field === 'seqNo' ? sectorSort.direction : 'asc'}
                            onClick={() => handleSectorSort('seqNo')}
                          >
                            Sector
                          </TableSortLabel>
                        </TableCell>
                        <TableCell sortDirection={sectorSort.field === 'route' ? sectorSort.direction : false}>
                          <TableSortLabel
                            active={sectorSort.field === 'route'}
                            direction={sectorSort.field === 'route' ? sectorSort.direction : 'asc'}
                            onClick={() => handleSectorSort('route')}
                          >
                            Route
                          </TableSortLabel>
                        </TableCell>
                        <TableCell sortDirection={sectorSort.field === 'aircraft' ? sectorSort.direction : false}>
                          <TableSortLabel
                            active={sectorSort.field === 'aircraft'}
                            direction={sectorSort.field === 'aircraft' ? sectorSort.direction : 'asc'}
                            onClick={() => handleSectorSort('aircraft')}
                          >
                            Aircraft
                          </TableSortLabel>
                        </TableCell>
                        <TableCell sortDirection={sectorSort.field === 'pilots' ? sectorSort.direction : false}>
                          <TableSortLabel
                            active={sectorSort.field === 'pilots'}
                            direction={sectorSort.field === 'pilots' ? sectorSort.direction : 'asc'}
                            onClick={() => handleSectorSort('pilots')}
                          >
                            Pilots
                          </TableSortLabel>
                        </TableCell>
                        <TableCell sortDirection={sectorSort.field === 'status' ? sectorSort.direction : false}>
                          <TableSortLabel
                            active={sectorSort.field === 'status'}
                            direction={sectorSort.field === 'status' ? sectorSort.direction : 'asc'}
                            onClick={() => handleSectorSort('status')}
                          >
                            Status
                          </TableSortLabel>
                        </TableCell>
                        <TableCell sortDirection={sectorSort.field === 'tripKit' ? sectorSort.direction : false}>
                          <TableSortLabel
                            active={sectorSort.field === 'tripKit'}
                            direction={sectorSort.field === 'tripKit' ? sectorSort.direction : 'asc'}
                            onClick={() => handleSectorSort('tripKit')}
                          >
                            Download TripKit
                          </TableSortLabel>
                        </TableCell>
                        <TableCell sortDirection={sectorSort.field === 'weather' ? sectorSort.direction : false}>
                          <TableSortLabel
                            active={sectorSort.field === 'weather'}
                            direction={sectorSort.field === 'weather' ? sectorSort.direction : 'asc'}
                            onClick={() => handleSectorSort('weather')}
                          >
                            BA / weather
                          </TableSortLabel>
                        </TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {sortedTripSectors.map((sector) => {
                        const tripKitList = extractTripKitList(sector);
                        const weatherList = extractWeatherMediaList(sector);
                        return (
                          <TableRow key={sector._id}>
                            <TableCell>{sector.seqNo}</TableCell>
                            <TableCell>{formatRoute(sector)}</TableCell>
                            <TableCell>{getAircraftName(sector.aircraftId, aircraft)}</TableCell>
                            <TableCell>{getPilotNames(sector.assignedPilots || [], pilots || [])}</TableCell>
                            <TableCell>
                              <Chip size="small" label={sector.status || 'N/A'} color={getStatusColor(sector.status)} />
                            </TableCell>
                            <TableCell>
                              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                                {tripKitList.map((media, idx) => (
                                  <Button
                                    key={media?._id || `${sector._id}-tripkit-${idx}`}
                                    size="small"
                                    variant="outlined"
                                    href={media?.fileUrl}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                  >
                                    {getMediaFileName(media, `TripKit ${idx + 1}`)}
                                  </Button>
                                ))}
                                {tripKitList.length === 0 && (
                                  <Typography variant="caption" color="text.secondary">None</Typography>
                                )}
                              </Box>
                            </TableCell>
                            <TableCell>
                              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                                <Button
                                  size="small"
                                  variant="outlined"
                                  onClick={() => handleDownloadBaVideo(sector)}
                                >
                                  Download BA Video
                                </Button>
                                {weatherList.map((media, idx) => (
                                  <Button
                                    key={media?._id || `${sector._id}-weather-${idx}`}
                                    size="small"
                                    variant="outlined"
                                    href={media?.fileUrl}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                  >
                                    {getMediaFileName(media, `Weather ${idx + 1}`)}
                                  </Button>
                                ))}
                                {weatherList.length === 0 && (
                                  <Typography variant="caption" color="text.secondary">No weather file</Typography>
                                )}
                              </Box>
                            </TableCell>
                          </TableRow>
                        );
                      })}
                      {!loadingTripDetail && tripSectors.length === 0 && (
                        <TableRow>
                          <TableCell colSpan={7} align="center">No sectors found for this charter</TableCell>
                        </TableRow>
                      )}
                    </TableBody>
                  </Table>
                </TableContainer>
              </>
            )}
          </CardContent>
        </Card>
      </Grid>
    </Grid>
  );
};

const CharterHistoryByPilot = ({ pilots, aircraft, showToast, buildErrorMessage }) => {
  const [selectedPilotId, setSelectedPilotId] = useState('');
  const [loading, setLoading] = useState(false);
  const [pilotSectors, setPilotSectors] = useState([]);
  const [statusFilter, setStatusFilter] = useState('');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [pilotSort, setPilotSort] = useState({ field: 'date', direction: 'desc' });

  const activePilots = useMemo(
    () => (pilots || []).filter((pilot) => pilot.isPilotActive && pilot.isActive !== false),
    [pilots]
  );

  const fetchPilotHistory = async (pilotId) => {
    if (!pilotId) {
      setPilotSectors([]);
      return;
    }
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      const query = statusFilter ? `?status=${encodeURIComponent(statusFilter)}` : '';
      const response = await axios.get(`/api/ops/charter-history/pilots/${pilotId}${query}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setPilotSectors(response.data || []);
    } catch (error) {
      showToast(buildErrorMessage('Load pilot history', error), 'error');
      setPilotSectors([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (selectedPilotId) fetchPilotHistory(selectedPilotId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedPilotId, statusFilter]);

  const handlePilotSort = (field) => {
    setPilotSort((prev) => ({
      field,
      direction: prev.field === field && prev.direction === 'asc' ? 'desc' : 'asc'
    }));
  };

  const pilotSectorsInDateRange = useMemo(() => {
    const fromD = dateFrom ? new Date(`${dateFrom}T00:00:00`) : null;
    const toD = dateTo ? new Date(`${dateTo}T23:59:59`) : null;
    return (pilotSectors || []).filter((s) => {
      const raw = s.departure?.date || s.createdAt;
      if (!raw) {
        return !(fromD || toD);
      }
      const sectorDate = new Date(raw);
      if (Number.isNaN(sectorDate.getTime())) {
        return !(fromD || toD);
      }
      if (fromD && sectorDate < fromD) return false;
      if (toD && sectorDate > toD) return false;
      return true;
    });
  }, [dateFrom, dateTo, pilotSectors]);

  const sortedPilotSectors = useMemo(() => (
    sortRows(pilotSectorsInDateRange || [], pilotSort, {
      date: (s) => s.departure?.date ? new Date(s.departure.date).getTime() : (s.createdAt ? new Date(s.createdAt).getTime() : 0),
      charter: (s) => (typeof s.tripId === 'object' && s.tripId?.charterNumber) || '',
      route: (s) => formatRoute(s),
      aircraft: (s) => getAircraftName(s.aircraftId, aircraft),
      crew: (s) => getPilotNames(s.assignedPilots || [], pilots || []),
      status: (s) => s.status || '',
      tripKit: (s) => extractTripKitList(s).length,
      baVideo: (s) => (s?.baMediaId?.fileUrl ? 1 : 0)
    })
  ), [pilotSectorsInDateRange, pilotSort, aircraft, pilots]);

  const handleDownloadBaVideo = async (sector) => {
    const directUrl = sector?.baMediaId?.fileUrl;
    if (directUrl) {
      window.open(directUrl, '_blank', 'noopener,noreferrer');
      return;
    }
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`/api/media/entity/Sector/${sector._id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const baMedia = (response.data || []).find((item) => item.kind === 'BA_VIDEO' && item.fileUrl);
      if (!baMedia) {
        showToast(`No BA video found for Sector ${sector.seqNo}.`, 'info');
        return;
      }
      window.open(baMedia.fileUrl, '_blank', 'noopener,noreferrer');
    } catch (error) {
      showToast(buildErrorMessage('Download BA video', error), 'error');
    }
  };

  const handleExportPilotCsv = () => {
    if (sortedPilotSectors.length === 0) {
      showToast('No pilot history data available to export.', 'info');
      return;
    }

    const rows = sortedPilotSectors.map((sector) => ([
      formatDateTime(sector.departure?.date || sector.createdAt),
      (typeof sector.tripId === 'object' && sector.tripId?.charterNumber) || 'N/A',
      formatRoute(sector),
      getAircraftName(sector.aircraftId, aircraft) || 'N/A',
      getPilotNames(sector.assignedPilots || [], pilots || []) || 'N/A',
      sector.status || 'N/A'
    ]));

    downloadCsv(
      `pilot-history-${new Date().toISOString().slice(0, 10)}.csv`,
      ['Date', 'Charter', 'Route', 'Aircraft', 'Crew', 'Status'],
      rows
    );
  };

  return (
    <Card>
      <CardContent>
        <Typography variant="h6" gutterBottom>Filter by Pilot</Typography>
        <Grid container spacing={2} sx={{ mb: 2 }}>
          <Grid item xs={12} md={6}>
            <FormControl fullWidth size="small">
              <InputLabel>Select Pilot</InputLabel>
              <Select
                label="Select Pilot"
                value={selectedPilotId}
                onChange={(e) => setSelectedPilotId(e.target.value)}
              >
                {activePilots.map((pilot) => (
                  <MenuItem key={pilot._id} value={pilot._id}>
                    {`${pilot.firstName || ''} ${pilot.lastName || ''}`.trim() || pilot.email}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} md={12}>
            <FormControl fullWidth size="small">
              <InputLabel>Status</InputLabel>
              <Select
                label="Status"
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
              >
                <MenuItem value="">All</MenuItem>
                <MenuItem value="CLEARED">CLEARED</MenuItem>
                <MenuItem value="ATC_PASSED">ATC_PASSED</MenuItem>
                <MenuItem value="COMPLETED">COMPLETED</MenuItem>
                <MenuItem value="SECTOR_COMPLETED">SECTOR_COMPLETED</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} md={4}>
            <Button
              fullWidth
              size="medium"
              variant="outlined"
              sx={{ minHeight: 40, py: 1 }}
              onClick={() => {
                setStatusFilter('');
                setDateFrom('');
                setDateTo('');
              }}
            >
              Clear filters
            </Button>
          </Grid>
          <Grid item xs={12} md={4}>
            <Button
              fullWidth
              size="medium"
              variant="contained"
              sx={{ minHeight: 40, py: 1 }}
              onClick={handleExportPilotCsv}
            >
              Download CSV
            </Button>
          </Grid>
          <Grid item xs={12} sm={6} md={4}>
            <TextField
              fullWidth
              size="small"
              label="From"
              type="date"
              InputLabelProps={{ shrink: true }}
              value={dateFrom}
              onChange={(e) => setDateFrom(e.target.value)}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={4}>
            <TextField
              fullWidth
              size="small"
              label="To"
              type="date"
              InputLabelProps={{ shrink: true }}
              value={dateTo}
              onChange={(e) => setDateTo(e.target.value)}
            />
          </Grid>
        </Grid>

        <TableContainer component={Paper}>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell sortDirection={pilotSort.field === 'date' ? pilotSort.direction : false}>
                  <TableSortLabel
                    active={pilotSort.field === 'date'}
                    direction={pilotSort.field === 'date' ? pilotSort.direction : 'asc'}
                    onClick={() => handlePilotSort('date')}
                  >
                    Date
                  </TableSortLabel>
                </TableCell>
                <TableCell sortDirection={pilotSort.field === 'charter' ? pilotSort.direction : false}>
                  <TableSortLabel
                    active={pilotSort.field === 'charter'}
                    direction={pilotSort.field === 'charter' ? pilotSort.direction : 'asc'}
                    onClick={() => handlePilotSort('charter')}
                  >
                    Charter
                  </TableSortLabel>
                </TableCell>
                <TableCell sortDirection={pilotSort.field === 'route' ? pilotSort.direction : false}>
                  <TableSortLabel
                    active={pilotSort.field === 'route'}
                    direction={pilotSort.field === 'route' ? pilotSort.direction : 'asc'}
                    onClick={() => handlePilotSort('route')}
                  >
                    Route
                  </TableSortLabel>
                </TableCell>
                <TableCell sortDirection={pilotSort.field === 'aircraft' ? pilotSort.direction : false}>
                  <TableSortLabel
                    active={pilotSort.field === 'aircraft'}
                    direction={pilotSort.field === 'aircraft' ? pilotSort.direction : 'asc'}
                    onClick={() => handlePilotSort('aircraft')}
                  >
                    Aircraft
                  </TableSortLabel>
                </TableCell>
                <TableCell sortDirection={pilotSort.field === 'crew' ? pilotSort.direction : false}>
                  <TableSortLabel
                    active={pilotSort.field === 'crew'}
                    direction={pilotSort.field === 'crew' ? pilotSort.direction : 'asc'}
                    onClick={() => handlePilotSort('crew')}
                  >
                    Crew
                  </TableSortLabel>
                </TableCell>
                <TableCell sortDirection={pilotSort.field === 'status' ? pilotSort.direction : false}>
                  <TableSortLabel
                    active={pilotSort.field === 'status'}
                    direction={pilotSort.field === 'status' ? pilotSort.direction : 'asc'}
                    onClick={() => handlePilotSort('status')}
                  >
                    Status
                  </TableSortLabel>
                </TableCell>
                <TableCell sortDirection={pilotSort.field === 'tripKit' ? pilotSort.direction : false}>
                  <TableSortLabel
                    active={pilotSort.field === 'tripKit'}
                    direction={pilotSort.field === 'tripKit' ? pilotSort.direction : 'asc'}
                    onClick={() => handlePilotSort('tripKit')}
                  >
                    Download TripKit
                  </TableSortLabel>
                </TableCell>
                <TableCell sortDirection={pilotSort.field === 'baVideo' ? pilotSort.direction : false}>
                  <TableSortLabel
                    active={pilotSort.field === 'baVideo'}
                    direction={pilotSort.field === 'baVideo' ? pilotSort.direction : 'asc'}
                    onClick={() => handlePilotSort('baVideo')}
                  >
                    BA Video
                  </TableSortLabel>
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {sortedPilotSectors.map((sector) => {
                const tripKitList = extractTripKitList(sector);
                return (
                  <TableRow key={sector._id}>
                    <TableCell>{formatDateTime(sector.departure?.date || sector.createdAt)}</TableCell>
                    <TableCell>
                      {(typeof sector.tripId === 'object' && sector.tripId?.charterNumber) || 'N/A'}
                    </TableCell>
                    <TableCell>{formatRoute(sector)}</TableCell>
                    <TableCell>{getAircraftName(sector.aircraftId, aircraft)}</TableCell>
                    <TableCell>{getPilotNames(sector.assignedPilots || [], pilots || [])}</TableCell>
                    <TableCell>
                      <Chip size="small" label={sector.status || 'N/A'} color={getStatusColor(sector.status)} />
                    </TableCell>
                    <TableCell>
                      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                        {tripKitList.map((media, idx) => (
                          <Button
                            key={media?._id || `${sector._id}-tripkit-${idx}`}
                            size="small"
                            variant="outlined"
                            href={media?.fileUrl}
                            target="_blank"
                            rel="noopener noreferrer"
                          >
                            {getMediaFileName(media, `TripKit ${idx + 1}`)}
                          </Button>
                        ))}
                        {tripKitList.length === 0 && (
                          <Typography variant="caption" color="text.secondary">None</Typography>
                        )}
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Button
                        size="small"
                        variant="outlined"
                        onClick={() => handleDownloadBaVideo(sector)}
                      >
                        Download BA Video
                      </Button>
                    </TableCell>
                  </TableRow>
                );
              })}
              {!loading && selectedPilotId && sortedPilotSectors.length === 0 && (
                <TableRow>
                  <TableCell colSpan={8} align="center">
                    {pilotSectors.length === 0
                      ? 'No flights found for this pilot'
                      : 'No flights in this date range (adjust From / To or Clear filters)'}
                  </TableCell>
                </TableRow>
              )}
              {!selectedPilotId && (
                <TableRow>
                  <TableCell colSpan={8} align="center">Select a pilot to view flight history</TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </CardContent>
    </Card>
  );
};

const CharterHistoryView = ({ pilots, aircraft, showToast, buildErrorMessage }) => {
  const [historyMode, setHistoryMode] = useState('charter');
  return (
    <Box>
      <Tabs value={historyMode} onChange={(e, v) => setHistoryMode(v)} sx={{ mb: 2 }}>
        <Tab value="charter" label="Filter by Charter" />
        <Tab value="pilot" label="Filter by Pilot" />
      </Tabs>
      {historyMode === 'charter' && (
        <CharterHistoryByCharter
          pilots={pilots}
          aircraft={aircraft}
          showToast={showToast}
          buildErrorMessage={buildErrorMessage}
        />
      )}
      {historyMode === 'pilot' && (
        <CharterHistoryByPilot
          pilots={pilots}
          aircraft={aircraft}
          showToast={showToast}
          buildErrorMessage={buildErrorMessage}
        />
      )}
    </Box>
  );
};

export default CharterHistoryView;
